import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  listMailingCampaigns,
  createMailingCampaign,
  updateMailingCampaign,
  deleteMailingCampaign,
  sendBrevoMarketingCampaign,
  sendTestBrevoCampaign,
  getBrevoCampaignStats,
  applyCampaignTemplate,
  type MailingCampaign,
  type BrevoCampaignStats,
} from "@/lib/mailing-campaigns.functions";
import { listImportedRecipients } from "@/lib/brevo.functions";
import { supabase } from "@/integrations/supabase/client";
import { toWebp } from "@/lib/image-webp";
import { MailingMediaLibrary } from "./MailingMediaLibrary";

function fmt(iso: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  const date = d.toLocaleDateString("sl-SI", { day: "2-digit", month: "2-digit", year: "numeric" });
  const time = d.toLocaleTimeString("sl-SI", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${date} ob ${time}`;
}

export function MailingCampaignsSection() {
  const qc = useQueryClient();
  const fetchList = useServerFn(listMailingCampaigns);
  const createFn = useServerFn(createMailingCampaign);
  const { data, isLoading } = useQuery({
    queryKey: ["mailing-campaigns"],
    queryFn: () => fetchList(),
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newName, setNewName] = useState("");

  const createMut = useMutation({
    mutationFn: async () => {
      const name = newName.trim();
      if (!name) throw new Error("Vnesite ime kampanje.");
      return createFn({ data: { name } });
    },
    onSuccess: (r) => {
      toast.success("Kampanija ustvarjena.");
      setNewName("");
      setSelectedId(r.id);
      qc.invalidateQueries({ queryKey: ["mailing-campaigns"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Napaka."),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Nalagam…</p>;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <MailingMediaLibrary />
      <div className="border border-border p-6 space-y-3">

        <h2 className="font-serif text-2xl">Brevo kampanije</h2>
        <p className="text-sm text-muted-foreground">
          Kampanije se pošljejo neposredno preko Brevo na izbrane skupine prejemnikov
          (iz seznama "Prejemniki"). Statistike (odpiranja, kliki, odjave) pridobiš z gumbom "Osveži statistiko".
        </p>
        <div className="grid gap-3 md:grid-cols-[1fr_auto] items-end">
          <label className="block">
            <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">
              Nova kampanija — ime
            </span>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="npr. Pomladna ponudba 2026"
              className="w-full border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <button
            onClick={() => createMut.mutate()}
            disabled={createMut.isPending || !newName.trim()}
            className="bg-accent text-accent-foreground px-5 py-2 text-sm hover:opacity-90 disabled:opacity-50"
          >
            {createMut.isPending ? "Ustvarjam…" : "Ustvari"}
          </button>
        </div>
      </div>

      {data.campaigns.length === 0 ? (
        <p className="text-sm text-muted-foreground">Še ni kampanij. Ustvari prvo zgoraj.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {data.campaigns.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setSelectedId(selectedId === c.id ? null : c.id)}
              className={`border border-border px-3 py-1.5 text-sm transition-colors ${
                selectedId === c.id
                  ? "bg-accent text-accent-foreground"
                  : "bg-background hover:bg-muted"
              }`}
            >
              {c.name}
              {c.brevo_campaign_id ? (
                <span className="ml-2 text-xs opacity-70">#{c.brevo_campaign_id}</span>
              ) : null}
            </button>
          ))}
        </div>
      )}

      {selectedId &&
        data.campaigns
          .filter((c) => c.id === selectedId)
          .map((c) => <CampaignCard key={c.id} campaign={c} onDeleted={() => setSelectedId(null)} />)}
    </div>
  );
}

function CampaignCard({
  campaign,
  onDeleted,
}: {
  campaign: MailingCampaign;
  onDeleted: () => void;
}) {
  const qc = useQueryClient();
  const updateFn = useServerFn(updateMailingCampaign);
  const deleteFn = useServerFn(deleteMailingCampaign);
  const sendBrevoFn = useServerFn(sendBrevoMarketingCampaign);
  const sendTestFn = useServerFn(sendTestBrevoCampaign);
  const statsFn = useServerFn(getBrevoCampaignStats);
  const applyTplFn = useServerFn(applyCampaignTemplate);
  const fetchImported = useServerFn(listImportedRecipients);
  const labelsQ = useQuery({
    queryKey: ["marketing-recipients-summary"],
    queryFn: () => fetchImported(),
  });

  const [name, setName] = useState(campaign.name);
  const [subject, setSubject] = useState(campaign.subject);
  const [bodyHtml, setBodyHtml] = useState(campaign.body_html);
  const [senderName, setSenderName] = useState(campaign.sender_name);
  const [senderEmail, setSenderEmail] = useState(campaign.sender_email);
  const [targetLabels, setTargetLabels] = useState<string[]>(campaign.target_labels ?? []);
  const [stats, setStats] = useState<BrevoCampaignStats | null>(
    (campaign.brevo_last_stats as BrevoCampaignStats | null) ?? null,
  );
  const [testEmail, setTestEmail] = useState("");
  const [testFeedback, setTestFeedback] = useState<{
    type: "info" | "success" | "error";
    message: string;
  } | null>(null);
  const [repeatEnabled, setRepeatEnabled] = useState<boolean>(campaign.repeat_enabled ?? false);
  const [repeatDays, setRepeatDays] = useState<number>(campaign.repeat_interval_days ?? 7);
  const [repeatTime, setRepeatTime] = useState<string>(campaign.repeat_send_time ?? "09:00");
  const toLocalInput = (iso: string | null | undefined): string => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const [firstSendLocal, setFirstSendLocal] = useState<string>(toLocalInput((campaign as any).first_send_at));

  useEffect(() => {
    setName(campaign.name);
    setSubject(campaign.subject);
    setBodyHtml(campaign.body_html);
    setSenderName(campaign.sender_name);
    setSenderEmail(campaign.sender_email);
    setTargetLabels(campaign.target_labels ?? []);
    setStats((campaign.brevo_last_stats as BrevoCampaignStats | null) ?? null);
    setRepeatEnabled(campaign.repeat_enabled ?? false);
    setRepeatDays(campaign.repeat_interval_days ?? 7);
    setRepeatTime(campaign.repeat_send_time ?? "09:00");
    setFirstSendLocal(toLocalInput((campaign as any).first_send_at));
  }, [campaign.id, campaign.updated_at]);

  const firstSendIso = (): string | null => {
    if (!firstSendLocal) return null;
    const d = new Date(firstSendLocal);
    return isNaN(d.getTime()) ? null : d.toISOString();
  };

  const targetCount = (labelsQ.data?.labels ?? [])
    .filter((l) => targetLabels.includes(l.label))
    .reduce((a, b) => a + b.count, 0);

  const toggleLabel = (l: string) =>
    setTargetLabels((cur) => (cur.includes(l) ? cur.filter((x) => x !== l) : [...cur, l]));

  const saveMut = useMutation({
    mutationFn: () =>
      updateFn({
        data: {
          id: campaign.id,
          name,
          subject,
          body_html: bodyHtml,
          sender_name: senderName,
          sender_email: senderEmail,
          target_labels: targetLabels,
          repeat_enabled: repeatEnabled,
          repeat_interval_days: repeatEnabled ? repeatDays : null,
          repeat_send_time: repeatEnabled ? repeatTime : null,
          first_send_at: firstSendIso(),
        },
      }),
    onSuccess: () => {
      toast.success("Shranjeno.");
      qc.invalidateQueries({ queryKey: ["mailing-campaigns"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Napaka pri shranjevanju."),
  });

  const sendMut = useMutation({
    mutationFn: async () => {
      if (targetLabels.length === 0) throw new Error("Izberite vsaj eno ciljno skupino.");
      const ok = window.confirm(
        `Poslati kampanijo "${name}" preko Brevo (~${targetCount} prejemnikov)?\n\nPošiljatelj: ${senderName} <${senderEmail}>`,
      );
      if (!ok) throw new Error("Preklicano.");
      await updateFn({
        data: {
          id: campaign.id,
          name, subject, body_html: bodyHtml,
          sender_name: senderName, sender_email: senderEmail,
          target_labels: targetLabels,
          repeat_enabled: repeatEnabled,
          repeat_interval_days: repeatEnabled ? repeatDays : null,
          repeat_send_time: repeatEnabled ? repeatTime : null,
          first_send_at: firstSendIso(),
        },
      });
      return sendBrevoFn({ data: { campaignId: campaign.id } });
    },
    onSuccess: (r) => {
      toast.success(`Brevo kampanija sprožena (ID ${r.brevoCampaignId}, ${r.recipients} prejemnikov).`);
      qc.invalidateQueries({ queryKey: ["mailing-campaigns"] });
    },
    onError: (e: any) => {
      if (e?.message === "Preklicano.") return;
      toast.error(e?.message ?? "Brevo pošiljanje ni uspelo.");
    },
  });

  const statsMut = useMutation({
    mutationFn: () => statsFn({ data: { campaignId: campaign.id } }),
    onSuccess: (r) => {
      setStats(r);
      if (!r) toast.info("Ta kampanija še ni bila poslana preko Brevo.");
      else toast.success("Statistika osvežena.");
    },
    onError: (e: any) => toast.error(e?.message ?? "Napaka."),
  });
  const testMut = useMutation({
    mutationFn: async () => {
      const to = testEmail.trim();
      if (!to) throw new Error("Vnesite e-poštni naslov za test.");
      if (!senderName.trim()) throw new Error("Vnesite ime pošiljatelja.");
      if (!senderEmail.trim()) throw new Error("Vnesite e-poštni naslov pošiljatelja.");
      if (!subject.trim() || !bodyHtml.trim()) {
        throw new Error("Vnesite zadevo in vsebino kampanje pred testnim pošiljanjem.");
      }
      return sendTestFn({
        data: {
          campaignId: campaign.id,
          toEmail: to,
          subject,
          body_html: bodyHtml,
          sender_name: senderName,
          sender_email: senderEmail,
        },
      });
    },
    onMutate: () => {
      setTestFeedback({ type: "info", message: "Pošiljam testno sporočilo…" });
    },
    onSuccess: (r) => {
      const message = `Testno sporočilo poslano na ${r.to}.`;
      setTestFeedback({ type: "success", message });
      toast.success(message);
    },
    onError: (e: any) => {
      const message = e?.message ?? "Testno pošiljanje ni uspelo.";
      setTestFeedback({ type: "error", message });
      toast.error(message);
    },
  });


  const applyTplMut = useMutation({
    mutationFn: async (opts: { templateKey: "fresh" | "bestsellers" | "trending" | "stories"; overwriteName: boolean }) => {
      return applyTplFn({
        data: { campaignId: campaign.id, templateKey: opts.templateKey, overwriteName: opts.overwriteName },
      });
    },
    onSuccess: (r) => {
      setSubject(r.subject);
      setBodyHtml(r.body_html);
      toast.success("Predloga obnovljena — preglej in shrani.");
      qc.invalidateQueries({ queryKey: ["mailing-campaigns"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Predloge ni bilo mogoče naložiti."),
  });

  // Auto-populate body on first open if campaign was seeded with a
  // template_key but no body yet (initial state from migration).
  useEffect(() => {
    if (
      campaign.template_key &&
      (!campaign.body_html || campaign.body_html.trim().length === 0) &&
      !applyTplMut.isPending
    ) {
      applyTplMut.mutate({
        templateKey: campaign.template_key as any,
        overwriteName: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign.id]);

  const deleteMut = useMutation({
    mutationFn: () => deleteFn({ data: { id: campaign.id } }),
    onSuccess: () => {
      toast.success("Kampanija izbrisana.");
      qc.invalidateQueries({ queryKey: ["mailing-campaigns"] });
      onDeleted();
    },
    onError: (e: any) => toast.error(e?.message ?? "Napaka."),
  });

  return (
    <div className="border border-border p-6 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">Ime kampanje</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full border border-border bg-background px-3 py-2 text-sm"
          />
        </div>
        <div className="text-xs text-muted-foreground pt-6">
          Zadnje pošiljanje: {fmt(campaign.last_sent_at)}
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <label className="block">
          <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">Pošiljatelj — ime</span>
          <input
            type="text"
            value={senderName}
            onChange={(e) => setSenderName(e.target.value)}
            className="w-full border border-border bg-background px-3 py-2 text-sm"
          />
        </label>
        <label className="block">
          <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">
            Pošiljatelj — e-pošta (Reply-To bo enak temu naslovu; mora biti potrjen v Brevu)
          </span>
          <input
            type="email"
            value={senderEmail}
            onChange={(e) => setSenderEmail(e.target.value)}
            className="w-full border border-border bg-background px-3 py-2 text-sm"
          />
        </label>
      </div>

      <label className="block">
        <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">Zadeva</span>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Zadeva sporočila"
          className="w-full border border-border bg-background px-3 py-2 text-sm"
        />
      </label>

      {campaign.template_key && (
        <div className="border border-border bg-muted/30 p-3 space-y-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              Predloga: <strong className="text-foreground">{campaign.template_key}</strong> — dinamični deli se ob pošiljanju samodejno napolnijo z aktualnimi podatki
            </span>
            <button
              type="button"
              onClick={() => {
                if (window.confirm("Obnoviti privzeto vsebino predloge? Trenutne ročne spremembe besedila bodo izgubljene.")) {
                  applyTplMut.mutate({ templateKey: campaign.template_key as any, overwriteName: false });
                }
              }}
              disabled={applyTplMut.isPending}
              className="border border-border bg-background px-3 py-1 text-xs hover:bg-muted disabled:opacity-50"
            >
              {applyTplMut.isPending ? "Obnavljam…" : "Obnovi privzeto predlogo"}
            </button>
          </div>
          <div className="text-[11px] text-muted-foreground space-y-1">
            <p className="font-medium text-foreground">Razpoložljivi placeholderji (vstavi v vsebino):</p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-0.5">
              <li><code className="font-mono">{"{{NEWEST_PRODUCTS}}"}</code> — mreža 4 najnovejših izdelkov</li>
              <li><code className="font-mono">{"{{BESTSELLERS}}"}</code> — top 3 prodajani (90 dni)</li>
              <li><code className="font-mono">{"{{TRENDING}}"}</code> — mreža 4 najbolj ogledanih</li>
              <li><code className="font-mono">{"{{ON_SALE}}"}</code> — mreža 4 izdelkov v akciji</li>
              <li><code className="font-mono">{"{{TOP_RATED}}"}</code> — mreža 4 najbolje ocenjenih</li>
              <li><code className="font-mono">{"{{RANDOM_PICKS}}"}</code> — mreža 4 naključno izbranih</li>
              <li><code className="font-mono">{"{{CATEGORY:slug}}"}</code> — 4 izdelki iz kategorije (npr. <code className="font-mono">{"{{CATEGORY:keramika}}"}</code>)</li>
              <li><code className="font-mono">{"{{NEW_VENDOR}}"}</code> — kartica nove trgovine tedna</li>
              <li><code className="font-mono">{"{{RANDOM_VENDOR}}"}</code> — kartica aktivne trgovine z izdelki, krajem in povezavo</li>
              <li><code className="font-mono">{"{{RANDOM_VENDOR_PRODUCTS}}"}</code> — mreža 4 izdelkov iz iste aktivne trgovine</li>
            </ul>
          </div>
        </div>
      )}



      <BodyEditor bodyHtml={bodyHtml} setBodyHtml={setBodyHtml} />

      <div className="space-y-2">
        <span className="block text-xs uppercase tracking-wider text-muted-foreground">
          Ciljne skupine — {targetCount} prejemnikov
        </span>
        {labelsQ.isLoading ? (
          <div className="text-sm text-muted-foreground">Nalagam skupine…</div>
        ) : (labelsQ.data?.labels ?? []).length === 0 ? (
          <div className="text-sm text-muted-foreground">
            Ni skupin prejemnikov. Najprej uvozi prejemnike v zavihku "Prejemniki".
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {(labelsQ.data?.labels ?? []).map((l) => {
              const active = targetLabels.includes(l.label);
              return (
                <button
                  key={l.label}
                  type="button"
                  onClick={() => toggleLabel(l.label)}
                  className={`border border-border px-3 py-1 text-xs transition-colors ${
                    active ? "bg-accent text-accent-foreground" : "bg-background hover:bg-muted"
                  }`}
                >
                  {l.label} ({l.count})
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="border border-border bg-muted/30 p-3 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <span className="block text-xs uppercase tracking-wider text-muted-foreground">
              Ponavljajoče pošiljanje
            </span>
            <p className="text-xs text-muted-foreground mt-1">
              Ko je vključeno, se kampanija samodejno ponovi vsakih N dni, dokler ponavljanja ne izklopiš.
            </p>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={repeatEnabled}
              onChange={(e) => setRepeatEnabled(e.target.checked)}
              className="h-4 w-4"
            />
            <span className="text-sm">{repeatEnabled ? "Vključeno" : "Izključeno"}</span>
          </label>
        </div>
        <div className="grid gap-3 md:grid-cols-2 items-end">
          <label className="block md:col-span-2">
            <span className="block text-[11px] text-muted-foreground mb-1">
              Datum in ura prvega pošiljanja (lokalni čas)
            </span>
            <input
              type="datetime-local"
              value={firstSendLocal}
              onChange={(e) => setFirstSendLocal(e.target.value)}
              disabled={!repeatEnabled}
              className="w-full max-w-xs border border-border bg-background px-3 py-2 text-sm disabled:opacity-50"
            />
            <span className="block text-[11px] text-muted-foreground mt-1">
              {firstSendLocal ? (
                <>Izbrano: <strong className="text-foreground">{fmt(firstSendIso() ?? "")}</strong></>
              ) : (
                "Določi, kdaj se pošlje prva e-pošta. Če pustiš prazno in kampanija še ni bila poslana, se pošlje ob naslednji preveritvi (~15 min)."
              )}
            </span>
          </label>
          <label className="block">
            <span className="block text-[11px] text-muted-foreground mb-1">Ponovi vsakih (dni)</span>
            <input
              type="number"
              min={1}
              max={365}
              value={repeatDays}
              onChange={(e) => setRepeatDays(Math.max(1, Math.min(365, Number(e.target.value) || 1)))}
              disabled={!repeatEnabled}
              className="w-32 border border-border bg-background px-3 py-2 text-sm disabled:opacity-50"
            />
          </label>
          <label className="block">
            <span className="block text-[11px] text-muted-foreground mb-1">Ura pošiljanja naslednjih ponovitev</span>
            <input
              type="time"
              value={repeatTime}
              onChange={(e) => setRepeatTime(e.target.value || "09:00")}
              disabled={!repeatEnabled}
              className="w-32 border border-border bg-background px-3 py-2 text-sm disabled:opacity-50"
            />
          </label>
          <div className="text-xs text-muted-foreground md:col-span-2">
            {repeatEnabled
              ? campaign.next_send_at
                ? <>Naslednje pošiljanje: <strong className="text-foreground">{fmt(campaign.next_send_at)}</strong></>
                : "Naslednje pošiljanje bo razporejeno po shranjevanju."
              : "Ponavljanje izklopljeno — pošlje se samo ročno."}
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Razporejene kampanije se preverjajo vsakih 15 minut, zato se lahko dejansko pošiljanje zamakne za do 15 min od izbrane ure. Spremembe potrdi z gumbom "Shrani".
        </p>
      </div>

      <div className="flex flex-wrap gap-2 pt-2">
        <button
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending}
          className="border border-border px-4 py-2 text-sm hover:bg-muted disabled:opacity-50"
        >
          {saveMut.isPending ? "Shranjujem…" : "Shrani"}
        </button>
        <button
          onClick={() => sendMut.mutate()}
          disabled={sendMut.isPending}
          className="bg-accent text-accent-foreground px-4 py-2 text-sm hover:opacity-90 disabled:opacity-50"
        >
          {sendMut.isPending ? "Pošiljam…" : "Pošlji preko Brevo"}
        </button>
        <button
          onClick={() => statsMut.mutate()}
          disabled={statsMut.isPending}
          className="border border-border px-4 py-2 text-sm hover:bg-muted disabled:opacity-50"
        >
          {statsMut.isPending ? "Osvežujem…" : "Osveži statistiko"}
        </button>
        <button
          onClick={() => {
            if (window.confirm(`Izbrisati kampanijo "${name}"?`)) deleteMut.mutate();
          }}
          disabled={deleteMut.isPending}
          className="border border-destructive text-destructive px-4 py-2 text-sm hover:bg-destructive/10 disabled:opacity-50 ml-auto"
        >
          Izbriši
        </button>
      </div>

      <div className="border border-border bg-muted/30 p-3 space-y-2">
        <span className="block text-xs uppercase tracking-wider text-muted-foreground">
          Testno pošiljanje — pošlji eno kopijo z aktualno vsebino iz urejevalnika, brez vpliva na ciljne skupine
        </span>
        <div className="flex flex-wrap gap-2 items-stretch">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => {
              setTestEmail(e.target.value);
              if (testFeedback?.type === "error") setTestFeedback(null);
            }}
            placeholder="ime@primer.si"
            className="flex-1 min-w-[220px] border border-border bg-background px-3 py-2 text-sm"
          />
          <button
            onClick={() => testMut.mutate()}
            disabled={testMut.isPending}
            className="border border-border bg-background px-4 py-2 text-sm hover:bg-muted disabled:opacity-50"
          >
            {testMut.isPending ? "Pošiljam test…" : "Pošlji testno"}
          </button>
        </div>
        {testFeedback ? (
          <p
            className={`text-sm ${
              testFeedback.type === "error"
                ? "text-destructive"
                : testFeedback.type === "success"
                  ? "text-green-700"
                  : "text-muted-foreground"
            }`}
          >
            {testFeedback.message}
          </p>
        ) : null}
      </div>

      {stats && (
        <div className="border border-border p-4 mt-2">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
            Brevo statistika (osveženo {fmt(stats.syncedAt)} — status: {stats.status})
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <StatBox label="Poslano" value={stats.sent} />
            <StatBox label="Dostavljeno" value={stats.delivered} />
            <StatBox label="Odprtja" value={stats.opens} sub={`unikatno: ${stats.uniqueOpens}`} />
            <StatBox label="Kliki" value={stats.clicks} sub={`unikatno: ${stats.uniqueClicks}`} />
            <StatBox label="Soft bounce" value={stats.softBounces} />
            <StatBox label="Hard bounce" value={stats.hardBounces} />
            <StatBox label="Odjave" value={stats.unsubscriptions} />
            <StatBox label="Pritožbe" value={stats.complaints} />
          </div>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, sub }: { label: string; value: number; sub?: string }) {
  return (
    <div className="border border-border p-3">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-serif text-2xl mt-1">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function textToHtml(text: string): string {
  const paragraphs = text.replace(/\r\n/g, "\n").split(/\n{2,}/);
  return paragraphs
    .map((p) => {
      const trimmed = p.trim();
      if (!trimmed) return "";
      return `<p>${escapeHtml(trimmed).replace(/\n/g, "<br />")}</p>`;
    })
    .filter(Boolean)
    .join("\n");
}

function htmlToText(html: string): string {
  if (!html) return "";
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>\s*<p[^>]*>/gi, "\n\n")
    .replace(/<\/?p[^>]*>/gi, "")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .trim();
}

function buildButtonHtml(label: string, url: string): string {
  const safeLabel = escapeHtml(label);
  const safeUrl = escapeHtml(url);
  return (
    `<table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin:16px 0;">` +
    `<tr><td style="border-radius:4px;background:#111111;">` +
    `<a href="${safeUrl}" target="_blank" rel="noopener" ` +
    `style="display:inline-block;padding:12px 22px;font-family:Arial,sans-serif;font-size:14px;` +
    `color:#ffffff;text-decoration:none;border-radius:4px;font-weight:600;letter-spacing:0.02em;">` +
    `${safeLabel}</a></td></tr></table>`
  );
}

function buildLinkHtml(label: string, url: string): string {
  return `<a href="${escapeHtml(url)}" target="_blank" rel="noopener" style="color:#111111;text-decoration:underline;">${escapeHtml(label)}</a>`;
}

function normalizeUrl(raw: string): string | null {
  const v = raw.trim();
  if (!v) return null;
  if (/^(https?:|mailto:|tel:|#|\/)/i.test(v)) return v;
  return `https://${v}`;
}

function BodyEditor({
  bodyHtml,
  setBodyHtml,
}: {
  bodyHtml: string;
  setBodyHtml: (v: string) => void;
}) {
  const [mode, setMode] = useState<"text" | "html">("html");
  const [textDraft, setTextDraft] = useState<string>(() => htmlToText(bodyHtml));
  const [insertOpen, setInsertOpen] = useState<null | "button" | "link">(null);
  const [insertLabel, setInsertLabel] = useState("");
  const [insertUrl, setInsertUrl] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const htmlRef = useState<{ el: HTMLTextAreaElement | null }>(() => ({ el: null }))[0];

  const insertSnippet = (snippet: string) => {
    if (mode === "text") {
      const currentHtml = textToHtml(textDraft);
      setBodyHtml(currentHtml + "\n" + snippet);
      setMode("html");
      return;
    }
    const ta = htmlRef.el;
    if (ta && typeof ta.selectionStart === "number") {
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const next = bodyHtml.slice(0, start) + snippet + bodyHtml.slice(end);
      setBodyHtml(next);
      requestAnimationFrame(() => {
        ta.focus();
        const pos = start + snippet.length;
        ta.setSelectionRange(pos, pos);
      });
    } else {
      setBodyHtml(bodyHtml + "\n" + snippet);
    }
  };

  const handleImageFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Slika je prevelika (max 10 MB).");
      return;
    }
    setUploadingImage(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Niste prijavljeni.");
      let upload: Blob = file;
      let ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      let contentType = file.type || "image/jpeg";
      try {
        const webp = await toWebp(file, { maxDim: 1600, quality: 0.85 });
        if (webp) {
          upload = webp;
          ext = "webp";
          contentType = "image/webp";
        }
      } catch { /* ignore */ }
      const path = `${user.id}/mailing/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error: upErr } = await supabase.storage.from("vendor-assets").upload(path, upload, {
        cacheControl: "3600",
        upsert: false,
        contentType,
      });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from("vendor-assets").getPublicUrl(path);
      const alt = (file.name.replace(/\.[^.]+$/, "") || "Slika").replace(/"/g, "");
      const snippet = `<img src="${data.publicUrl}" alt="${alt}" style="max-width:100%;height:auto;display:block;margin:16px auto;" />`;
      insertSnippet(snippet);
      toast.success("Slika vstavljena.");
    } catch (err) {
      toast.error((err as Error).message ?? "Nalaganje slike ni uspelo.");
    } finally {
      setUploadingImage(false);
    }
  };

  const switchTo = (next: "text" | "html") => {
    if (next === mode) return;
    if (next === "text") {
      setTextDraft(htmlToText(bodyHtml));
    } else {
      setBodyHtml(textToHtml(textDraft));
    }
    setMode(next);
  };

  const doInsert = () => {
    const url = normalizeUrl(insertUrl);
    const label = insertLabel.trim();
    if (!label) {
      toast.error("Vnesite besedilo gumba/povezave.");
      return;
    }
    if (!url) {
      toast.error("Vnesite URL.");
      return;
    }
    const snippet =
      insertOpen === "button" ? buildButtonHtml(label, url) : buildLinkHtml(label, url);

    if (mode === "text") {
      const currentHtml = textToHtml(textDraft);
      setBodyHtml(currentHtml + "\n" + snippet);
      setMode("html");
    } else {
      const ta = htmlRef.el;
      if (ta && typeof ta.selectionStart === "number") {
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const next = bodyHtml.slice(0, start) + snippet + bodyHtml.slice(end);
        setBodyHtml(next);
        requestAnimationFrame(() => {
          ta.focus();
          const pos = start + snippet.length;
          ta.setSelectionRange(pos, pos);
        });
      } else {
        setBodyHtml(bodyHtml + "\n" + snippet);
      }
    }

    setInsertOpen(null);
    setInsertLabel("");
    setInsertUrl("");
    toast.success(insertOpen === "button" ? "Gumb vstavljen." : "Povezava vstavljena.");
  };

  const openInsert = (kind: "button" | "link") => {
    setInsertOpen(kind);
    setInsertLabel(kind === "button" ? "Oglej si izdelke" : "Več informacij");
    setInsertUrl("https://kupish.si/");
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="block text-xs uppercase tracking-wider text-muted-foreground">
          Vsebina — Brevo doda unsubscribe footer, če manjka
        </span>
        <div className="flex flex-wrap gap-2 items-center">
          <button
            type="button"
            onClick={() => openInsert("button")}
            className="border border-border bg-background px-3 py-1 text-xs hover:bg-muted"
          >
            + Vstavi gumb
          </button>
          <button
            type="button"
            onClick={() => openInsert("link")}
            className="border border-border bg-background px-3 py-1 text-xs hover:bg-muted"
          >
            + Vstavi povezavo
          </button>
          <button
            type="button"
            onClick={() => imageInputRef.current?.click()}
            disabled={uploadingImage}
            className="border border-border bg-background px-3 py-1 text-xs hover:bg-muted disabled:opacity-50"
          >
            {uploadingImage ? "Nalagam…" : "+ Vstavi sliko"}
          </button>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageFile}
            className="hidden"
          />
          <div className="flex border border-border">
            <button
              type="button"
              onClick={() => switchTo("text")}
              className={`px-3 py-1 text-xs ${
                mode === "text" ? "bg-accent text-accent-foreground" : "bg-background hover:bg-muted"
              }`}
            >
              Besedilo
            </button>
            <button
              type="button"
              onClick={() => switchTo("html")}
              className={`px-3 py-1 text-xs border-l border-border ${
                mode === "html" ? "bg-accent text-accent-foreground" : "bg-background hover:bg-muted"
              }`}
            >
              HTML
            </button>
          </div>
        </div>
      </div>

      {insertOpen && (
        <div className="border border-border bg-muted/30 p-3 space-y-2">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">
            {insertOpen === "button" ? "Nov gumb" : "Nova povezava"}
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            <label className="block">
              <span className="block text-[11px] text-muted-foreground mb-1">Besedilo</span>
              <input
                type="text"
                value={insertLabel}
                onChange={(e) => setInsertLabel(e.target.value)}
                placeholder={insertOpen === "button" ? "npr. Oglej si izdelke" : "npr. Več informacij"}
                className="w-full border border-border bg-background px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="block text-[11px] text-muted-foreground mb-1">
                URL — npr. https://kupish.si/izdelki
              </span>
              <input
                type="text"
                value={insertUrl}
                onChange={(e) => setInsertUrl(e.target.value)}
                placeholder="https://…"
                className="w-full border border-border bg-background px-3 py-2 text-sm"
              />
            </label>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={doInsert}
              className="bg-accent text-accent-foreground px-3 py-1.5 text-xs hover:opacity-90"
            >
              Vstavi
            </button>
            <button
              type="button"
              onClick={() => setInsertOpen(null)}
              className="border border-border px-3 py-1.5 text-xs hover:bg-muted"
            >
              Prekliči
            </button>
          </div>
        </div>
      )}

      {mode === "text" ? (
        <textarea
          value={textDraft}
          onChange={(e) => {
            setTextDraft(e.target.value);
            setBodyHtml(textToHtml(e.target.value));
          }}
          rows={14}
          placeholder={"Pozdravljeni,\n\nVeseli nas, da …\n\nLep pozdrav,\nkupish.si"}
          className="w-full border border-border bg-background px-3 py-2 text-sm"
        />
      ) : (
        <textarea
          ref={(el) => {
            htmlRef.el = el;
          }}
          value={bodyHtml}
          onChange={(e) => setBodyHtml(e.target.value)}
          rows={14}
          placeholder="<p>Pozdravljeni,</p><p>…</p>"
          className="w-full border border-border bg-background px-3 py-2 text-xs font-mono"
        />
      )}
      <p className="text-xs text-muted-foreground">
        {mode === "text"
          ? "Vnesi navadno besedilo — prazna vrstica pomeni nov odstavek. Pred pošiljanjem se samodejno pretvori v HTML."
          : "Vnesi HTML. Lahko uporabiš <p>, <a>, <strong>, <ul>, <img> itd. Za hitro vstavljanje uporabi gumba zgoraj."}
      </p>

      <div className="space-y-2 pt-2">
        <span className="block text-xs uppercase tracking-wider text-muted-foreground">
          Predogled — tako bo videti v e-pošti (vključno s slikami)
        </span>
        <iframe
          title="Predogled e-pošte"
          srcDoc={`<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#111;margin:0;padding:16px;background:#fff;line-height:1.5}img{max-width:100%;height:auto}a{color:#1e6fd9}</style></head><body>${
            mode === "text" ? textToHtml(textDraft) : bodyHtml
          }</body></html>`}
          sandbox="allow-same-origin allow-popups"
          className="w-full h-[480px] border border-border bg-white"
        />
      </div>
    </div>
  );
}
